import type { ReactNode } from 'react';
import type { ClientMetric } from '../types';
import { Card, MicroLabel, SectionHead, cn } from '../ui/primitives';

/**
 * The account at a glance: a description, the figures strip, and a callout.
 *
 * ── Why the metrics are clickable ──
 *
 * Every figure counts something that is a section further down the same page.
 * Given a `sectionId`, the cell becomes the way to get there — the count and
 * the link to what it counts are one affordance, so there is no second control
 * to find. A metric with no section (a money total, say) stays inert rather
 * than growing a destination it doesn't have.
 *
 * The figures arrive FORMATTED. "Lifetime value" is money in a currency the
 * app chose, and "3 brands" may be a number the backend derived; deciding
 * either here would put business rules in a layout component.
 */
export function ClientOverview({
	description,
	metrics,
	onMetricClick,
	callout,
	action,
}: {
	description?: ReactNode;
	metrics?: ClientMetric[];
	/** Given a metric carrying a `sectionId`, jump to it. */
	onMetricClick?: (sectionId: string) => void;
	/**
	 * The panel under the strip — "client workspace active", an onboarding
	 * prompt, a warning. A slot because what is worth saying about an account
	 * differs per app and per viewer.
	 */
	callout?: ReactNode;
	action?: ReactNode;
}) {
	const cells = metrics ?? [];

	return (
		<>
			<SectionHead title="Overview" action={action} />
			<Card padded>
				{description && (
					<div className="mb-5 max-w-[40rem] text-sm leading-relaxed text-text-1">
						{description}
					</div>
				)}

				{cells.length > 0 && (
					<div className="mb-3.5 grid grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] gap-px overflow-hidden rounded-[var(--r-2)] border border-line-1 bg-line-1">
						{cells.map((metric) => {
							const jump = metric.sectionId;
							const interactive = Boolean(jump && onMetricClick);
							return (
								<button
									key={metric.label}
									type="button"
									disabled={!interactive}
									onClick={
										interactive
											? () => onMetricClick?.(jump as string)
											: undefined
									}
									className={cn(
										'bg-surface px-4 py-3 text-left transition-colors duration-[var(--dur-fast)]',
										interactive
											? 'cursor-pointer hover:bg-surface-hover'
											: 'cursor-default'
									)}
								>
									<MicroLabel className="mb-1.5 block">{metric.label}</MicroLabel>
									<span className="block whitespace-nowrap font-mono text-lg font-medium text-text-1">
										{metric.value}
									</span>
								</button>
							);
						})}
					</div>
				)}

				{callout}
			</Card>
		</>
	);
}

/**
 * The tinted panel the overview closes on.
 *
 * Exported separately so an app can use the same treatment elsewhere, and so
 * the overview itself stays a layout with a slot rather than growing a variant
 * prop per message it might carry.
 */
export function ClientCallout({
	icon,
	title,
	body,
	action,
}: {
	icon?: ReactNode;
	title: string;
	body?: ReactNode;
	action?: ReactNode;
}) {
	return (
		<div className="flex flex-wrap items-center gap-3.5 rounded-[var(--r-2)] border border-accent-line bg-accent-soft px-4 py-3.5">
			{icon && (
				<span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[var(--r-2)] border border-accent-line bg-surface text-accent">
					{icon}
				</span>
			)}
			<span className="min-w-[12rem] flex-1">
				<span className="block text-xs font-medium text-text-1">{title}</span>
				{body && <span className="mt-0.5 block text-2xs text-text-3">{body}</span>}
			</span>
			{action && <span className="shrink-0">{action}</span>}
		</div>
	);
}
